import { useState, useEffect, useRef } from 'react';

export function useLocalStorage<T>(key: string, initialValue: T) {
  // Use a ref to store the latest value for comparison without triggering re-renders
  const valueRef = useRef<T>(initialValue);
  
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      const parsedItem = item ? JSON.parse(item) : initialValue;
      valueRef.current = parsedItem;
      return parsedItem;
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  const setValue = (value: T | ((val: T) => T)) => {
    try {
      // Allow value to be a function for previous state pattern
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      
      // Only update state and localStorage if the value has actually changed
      const currentValueString = JSON.stringify(valueRef.current);
      const newValueString = JSON.stringify(valueToStore);
      
      if (currentValueString !== newValueString) {
        // Update state
        setStoredValue(valueToStore);
        
        // Update ref
        valueRef.current = valueToStore;
        
        // Update localStorage
        window.localStorage.setItem(key, newValueString);
        
        // Dispatch custom event for cross-tab synchronization
        window.dispatchEvent(new CustomEvent('localStorageChange', {
          detail: { key, value: valueToStore }
        }));
      }
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error);
    }
  };

  // Listen for storage changes from other tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === key && e.newValue !== null) {
        try {
          const newValue = JSON.parse(e.newValue);
          
          // Only update state if the value has actually changed
          const currentValueString = JSON.stringify(valueRef.current);
          const newValueString = JSON.stringify(newValue);
          
          if (currentValueString !== newValueString) {
            setStoredValue(newValue);
            valueRef.current = newValue;
          }
        } catch (error) {
          console.error(`Error parsing localStorage value for key "${key}":`, error);
        }
      }
    };

    const handleCustomStorageChange = (e: CustomEvent) => {
      if (e.detail.key === key) {
        // Only update state if the value has actually changed
        const currentValueString = JSON.stringify(valueRef.current);
        const newValueString = JSON.stringify(e.detail.value);
        
        if (currentValueString !== newValueString) {
          setStoredValue(e.detail.value);
          valueRef.current = e.detail.value;
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('localStorageChange', handleCustomStorageChange as EventListener);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageChange', handleCustomStorageChange as EventListener);
    };
  }, [key]);

  return [storedValue, setValue] as const;
}